import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles?: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'vks_token';
  private readonly TENANT_KEY = 'vks_tenant';
  private readonly USER_KEY = 'vks_user';

  isAuthenticated = signal(this.isTokenValid());
  currentUser = signal<AuthUser | null>(this.loadUser());

  constructor(private router: Router) {}

  async login(email: string, password: string, tenantId: string): Promise<void> {
    const res = await fetch(environment.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({
        query: `mutation Login($input: LoginInput!) {
          login(input: $input) {
            accessToken
            user { id email displayName roles }
          }
        }`,
        variables: { input: { email, password, tenantId } },
      }),
    });

    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message);
    }

    const { accessToken, user } = json.data.login;
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.TENANT_KEY, tenantId);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.isAuthenticated.set(true);
    this.currentUser.set(user);
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TENANT_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  hasRole(role: string): boolean {
    const user = this.currentUser();
    return user?.roles?.includes(role) ?? false;
  }

  setAuth(token: string, tenantId: string, user: AuthUser): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TENANT_KEY, tenantId);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.isAuthenticated.set(true);
    this.currentUser.set(user);
  }

  async oauthLogin(code: string, redirectUri: string, tenantId: string): Promise<void> {
    const res = await fetch(environment.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': tenantId,
      },
      body: JSON.stringify({
        query: `mutation OAuthLogin($input: OAuthLoginInput!) {
          oauthLogin(input: $input) {
            accessToken
            user { id email displayName roles }
          }
        }`,
        variables: {
          input: { provider: 'google', code, redirectUri, tenantId },
        },
      }),
    });

    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message);
    }

    const { accessToken, user } = json.data.oauthLogin;
    this.setAuth(accessToken, tenantId, user);
  }

  getToken(): string | null {
    if (!this.isTokenValid()) {
      if (localStorage.getItem(this.TOKEN_KEY)) {
        // Token exists but expired — clean up
        this.logout();
      }
      return null;
    }
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getTenantId(): string | null {
    return localStorage.getItem(this.TENANT_KEY);
  }

  private isTokenValid(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return true; // no expiry = treat as valid
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
